import {
  proStudioUrqlGql as urqlGql,
  SolutionVersion as GeneratedSolutionVersion,
} from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import {
  ProStudioEvents,
  ProStudioContext,
  SelectedSolution,
} from '../prostudio.xstate.types';
import {
  GetBotByIdData,
  GetBotData,
  GetEnvsList,
  GetVersionDataQuery,
  SolutionVersion,
} from '../prostudio.api-service.types';
import {
  checkForErrors,
  findDraftVersion,
  findLastDeployedVersion,
  getBotWithAssets,
} from '../../utils';
import { sendUserMessage } from '../app.xstate-utils';

export const completeVersions = (
  versions: string[],
  data: SolutionVersion[]
): SolutionVersion[] => {
  const existingVersions = new Set(data.map((item) => item.version));

  const missingEntries: SolutionVersion[] = versions
    .filter((version) => !existingVersions.has(version))
    .map((version) => ({
      bot: '',
      customer: '',
      email: '',
      env: '',
      first_name: '',
      last_name: '',
      time: '',
      uuid: '',
      version,
      picture: '',
    }));

  return [...data, ...missingEntries];
};

export const sortByVersionDescending = (
  data: SolutionVersion[]
): SolutionVersion[] => {
  return [...data].sort((a, b) =>
    (b.version as string).localeCompare(a.version as string, undefined, {
      numeric: true,
    })
  );
};

const checkInvalidPostHistoryResponse = ({
  botVersions,
  publishedVersions,
}: {
  botVersions: string[];
  publishedVersions: SolutionVersion[];
}): boolean => {
  const historyVersions = Array.from(
    new Set(publishedVersions.map((item) => item.version))
  );

  if (
    botVersions.length === 1 &&
    botVersions[0] === 'v1' &&
    historyVersions.length > 0
  ) {
    sendUserMessage({
      type: 'error',
      text: `
        Last version ${findLastDeployedVersion(botVersions)} was deployed with errors.
        Check your CSV file, fix errors and try to redeploy again please.
      `,
      autoClose: 3000,
    });
  }

  return true;
};

const DEFAULT_AVATAR_URL =
  'https://images.pexels.com/photos/2216542/pexels-photo-2216542.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2';

export const loadBotVersions = async (
  ctx: ProStudioContext,
  _event: ProStudioEvents
): Promise<{
  versions: SolutionVersion[];
  deployedVersions: GetBotData['getEnvBotVersions']['data']['envs'];
  selectedBot: SelectedSolution;
  draftVersion: string | null;
  envs: GetEnvsList['getListEnvs']['data'];
  draftData: GetVersionDataQuery['getVersionById']['data'] | null;
  draftVersionSolutionData: GeneratedSolutionVersion | null;
  solutionVersions: GeneratedSolutionVersion[];
  corpusSettings: {
    liveCorpusName: string;
    sandboxCorpusName: string;
  };
} | void> => {
  const {
    botName,
    customerId,
    selectedBot: ctxSelectedBot,
    project,
    solutions,
  } = ctx;

  const projectId = project?.id;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  if (!botName) {
    throwErrorWithId('Bot Name is required');
    return;
  }

  if (!customerId) {
    throwErrorWithId('Customer ID is required');
    return;
  }

  const botId =
    `${customerId}.${botName}` ||
    (ctxSelectedBot ? ctxSelectedBot.id : undefined);

  if (!botId) {
    throwErrorWithId('Bot ID is required');
    return;
  }

  const selectedSolution = solutions?.find(
    (solution) => solution?.name === botName
  );

  const solutionId = selectedSolution?.id;

  const [
    historyResponse,
    botResponse,
    botByIdResponse,
    envsResponse,
    solutionByIdResponse,
  ] = await Promise.all([
    urqlGql.postHistorySearch({
      customerId,
      projectId,
      searchBody: {
        bot: botName,
      },
    }),
    urqlGql.getBotData({ botId, projectId }),
    ctxSelectedBot ? undefined : urqlGql.getBotById({ botId, projectId }),
    urqlGql.getEnvsList({ projectId }),
    solutionId ? urqlGql.getSolutionById({ solutionByIdId: solutionId }) : null,
  ]);

  checkForErrors(historyResponse);
  checkForErrors(botResponse);
  if (botByIdResponse) {
    checkForErrors(botByIdResponse);
  }
  if (solutionByIdResponse) {
    checkForErrors(solutionByIdResponse);
  }
  checkForErrors(envsResponse);

  const botData =
    (botByIdResponse?.data?.getBotById?.data as GetBotByIdData) ||
    ctxSelectedBot;

  const selectedBot = {
    ...botData,
    uniqueId:
      solutions?.find((solution) => solution.name === botName)?.id || '',
    displayName:
      ctxSelectedBot?.displayName ||
      selectedSolution?.displayName ||
      'UNKNOWN SOLUTION NAME',
  };

  const selectedBotVersions = selectedBot.versions || [];
  const rawItems = historyResponse.data?.postHistorySearch?.data ?? [];
  const solution = solutionByIdResponse?.data?.admin_?.solutionById;
  const corpusSettings = solution?.settings;

  const extractLastIdFromUrl = (url: string): string => {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1] || '';
  };

  const transformedCorpusSettings = {
    liveCorpusName: corpusSettings?.liveCorpusName
      ? extractLastIdFromUrl(corpusSettings?.liveCorpusName)
      : '',
    sandboxCorpusName: corpusSettings?.sandboxCorpusName
      ? extractLastIdFromUrl(corpusSettings?.sandboxCorpusName)
      : '',
  };

  const getVersionsWithPictures = async (): Promise<SolutionVersion[]> => {
    // TODO: remove this check after fixing the API
    const isValidVersionsData = checkInvalidPostHistoryResponse({
      botVersions: selectedBotVersions,
      publishedVersions: rawItems,
    });

    if (!isValidVersionsData) {
      return [];
    }

    const versionsWithPictures = await Promise.all(
      rawItems.map(async (item: Omit<SolutionVersion, 'picture'>) => {
        try {
          const response = await urqlGql.getUserPictureByEmail({
            userEmail: item.email,
          });

          return {
            ...item,
            picture:
              response.data?.tenancy_users[0].picture || DEFAULT_AVATAR_URL,
          };
        } catch (error) {
          console.error(`Error getting picture for ${item.email}:`, error);
          return {
            ...item,
            picture: DEFAULT_AVATAR_URL,
          };
        }
      })
    );

    return versionsWithPictures;
  };

  const versionsWithPictures = await getVersionsWithPictures();

  // IMPORTANT: selectedBotVersions must be more than versionsWithPictures on 1 version, this version is a draft version
  const draftVersion =
    selectedBot.draftVersion ||
    // We're using this deprecated function here for safety (it might just be worth removing)
    findDraftVersion(selectedBotVersions, versionsWithPictures);

  const versions = sortByVersionDescending(
    completeVersions(selectedBotVersions, versionsWithPictures)
  ).filter(({ version }) => version !== draftVersion);

  let draftData = null;

  let draftVersionSolutionData = null;

  if (draftVersion) {
    const versionId = `${botId}.${draftVersion}`;

    const { data, error } = await urqlGql.getVersionById({
      versionId,
      projectId,
    });

    draftVersionSolutionData = solution?.solutionVersions?.find(
      (version) => version?.botSolutionVersionId === versionId
    ) as GeneratedSolutionVersion | null;

    if (error) {
      // if draft is not ready for deployment, it is an error from API
      console.error('VERSION_DATA_ERROR', error);
    }

    console.log('VERSION_DATA', data, draftVersionSolutionData);

    draftData = data?.getVersionById?.data;
  } else {
    throwErrorWithId(
      `Something went wrong: Draft version is required for ${botName} solution.`
    );
  }

  const botWithAssets = await getBotWithAssets({
    selectedBot,
    projectId,
  });

  const selectedBotWithAssets = {
    ...botWithAssets,
    botType: selectedBot.botType || 'main',
  };

  return {
    envs: envsResponse.data?.getListEnvs?.data || [],
    versions,
    deployedVersions: botResponse.data?.getEnvBotVersions?.data.envs,
    selectedBot: selectedBotWithAssets,
    draftVersion,
    draftData,
    draftVersionSolutionData,
    corpusSettings: transformedCorpusSettings,
    solutionVersions: (solution?.solutionVersions ||
      []) as GeneratedSolutionVersion[],
  };
};
